import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddDataCollectionPluginOptions,
  AddDataCollectionPluginResponse,
  CreateDataCollectionFieldOptions,
  CreateDataCollectionFieldResponse,
  DataCollection,
  DeleteDataCollectionFieldOptions,
  DeleteDataCollectionFieldResponse,
  DeleteDataCollectionPluginOptions,
  DeleteDataCollectionPluginResponse,
  Field,
  GetDataCollectionOptions,
  ListDataCollectionsOptions,
  ListDataCollectionsResponse,
  PatchDataCollection,
  PatchDataCollectionFieldResponse,
  PatchDataCollectionResponse,
  RestoreDataCollectionResponse,
  UpdateDataCollectionFieldOptions,
  UpdateDataCollectionFieldResponse,
  addDataCollectionPlugin as universalAddDataCollectionPlugin,
  createDataCollection as universalCreateDataCollection,
  createDataCollectionField as universalCreateDataCollectionField,
  deleteDataCollection as universalDeleteDataCollection,
  deleteDataCollectionField as universalDeleteDataCollectionField,
  deleteDataCollectionPlugin as universalDeleteDataCollectionPlugin,
  getDataCollection as universalGetDataCollection,
  listDataCollections as universalListDataCollections,
  patchDataCollection as universalPatchDataCollection,
  patchDataCollectionField as universalPatchDataCollectionField,
  restoreDataCollection as universalRestoreDataCollection,
  updateDataCollection as universalUpdateDataCollection,
  updateDataCollectionField as universalUpdateDataCollectionField,
} from './data-v2-data-collection-collections.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function createDataCollection(
  httpClient: HttpClient
): CreateDataCollectionSignature {
  return (
    collection: NonNullablePaths<
      DataCollection,
      | `_id`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `permissions.insert`
      | `permissions.read`
      | `permissions.remove`
      | `permissions.update`,
      4
    >
  ) =>
    universalCreateDataCollection(
      collection,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDataCollectionSignature {
  /**
   * Creates a new data collection.
   *
   * The request body must include an ID, details for at least 1 field, and a permissions object. If any of these are missing, the collection isn't created.
   * @param - Collection details.
   * @param - Options for creating a data collection.
   * @returns Details of collection created.
   */
  (
    collection: NonNullablePaths<
      DataCollection,
      | `_id`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `permissions.insert`
      | `permissions.read`
      | `permissions.remove`
      | `permissions.update`,
      4
    >
  ): Promise<
    NonNullablePaths<
      DataCollection,
      | `_id`
      | `collectionType`
      | `defaultDisplayOrder.fieldKey`
      | `defaultDisplayOrder.direction`
      | `capabilities.dataOperations`
      | `capabilities.collectionOperations`
      | `capabilities.indexLimits.regular`
      | `capabilities.indexLimits.unique`
      | `capabilities.indexLimits.total`
      | `fields`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `fields.${number}.typeMetadata.array.elementType`
      | `fields.${number}.systemField`
      | `fields.${number}.capabilities.sortable`
      | `fields.${number}.encrypted`
      | `fields.${number}.condition.permission`
      | `permissions.insert`
      | `permissions.update`
      | `permissions.remove`
      | `permissions.read`
      | `plugins`
      | `plugins.${number}.publishOptions.defaultStatus`
      | `plugins.${number}.singleItemOptions.singleItemId`
      | `plugins.${number}.urlizedOptions.format`
      | `plugins.${number}.editablePageLinkOptions.isPersisted`
      | `plugins.${number}.editablePageLinkOptions.isMutable`
      | `plugins.${number}.type`
      | `pagingModes`,
      6
    >
  >;
}

export function getDataCollection(
  httpClient: HttpClient
): GetDataCollectionSignature {
  return (dataCollectionId: string, options?: GetDataCollectionOptions) =>
    universalGetDataCollection(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDataCollectionSignature {
  /**
   * Retrieves a data collection by ID.
   * @param - ID of the collection to retrieve.
   * @param - Options for retrieving a data collection.
   * @returns Details of the collection requested.
   */
  (dataCollectionId: string, options?: GetDataCollectionOptions): Promise<
    NonNullablePaths<
      DataCollection,
      | `_id`
      | `collectionType`
      | `defaultDisplayOrder.fieldKey`
      | `defaultDisplayOrder.direction`
      | `capabilities.dataOperations`
      | `capabilities.collectionOperations`
      | `capabilities.indexLimits.regular`
      | `capabilities.indexLimits.unique`
      | `capabilities.indexLimits.total`
      | `fields`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `fields.${number}.typeMetadata.array.elementType`
      | `fields.${number}.systemField`
      | `fields.${number}.capabilities.sortable`
      | `fields.${number}.encrypted`
      | `fields.${number}.condition.permission`
      | `permissions.insert`
      | `permissions.update`
      | `permissions.remove`
      | `permissions.read`
      | `plugins`
      | `plugins.${number}.publishOptions.defaultStatus`
      | `plugins.${number}.singleItemOptions.singleItemId`
      | `plugins.${number}.urlizedOptions.format`
      | `plugins.${number}.editablePageLinkOptions.isPersisted`
      | `plugins.${number}.editablePageLinkOptions.isMutable`
      | `plugins.${number}.type`
      | `pagingModes`,
      6
    >
  >;
}

export function listDataCollections(
  httpClient: HttpClient
): ListDataCollectionsSignature {
  return (options?: ListDataCollectionsOptions) =>
    universalListDataCollections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDataCollectionsSignature {
  /**
   * Retrieves a list of all data collections associated with the site or project.
   *
   * By default, the list is ordered by ID in ascending order.
   * @param - Options for retrieving a list of data collections.
   */
  (options?: ListDataCollectionsOptions): Promise<
    NonNullablePaths<
      ListDataCollectionsResponse,
      | `collections`
      | `collections.${number}._id`
      | `collections.${number}.collectionType`
      | `collections.${number}.defaultDisplayOrder.fieldKey`
      | `collections.${number}.defaultDisplayOrder.direction`
      | `collections.${number}.capabilities.indexLimits.regular`
      | `collections.${number}.capabilities.indexLimits.unique`
      | `collections.${number}.capabilities.indexLimits.total`
      | `collections.${number}.permissions.insert`
      | `collections.${number}.permissions.update`
      | `collections.${number}.permissions.remove`
      | `collections.${number}.permissions.read`,
      6
    >
  >;
}

export function updateDataCollection(
  httpClient: HttpClient
): UpdateDataCollectionSignature {
  return (
    collection: NonNullablePaths<
      DataCollection,
      | `_id`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `permissions.insert`
      | `permissions.read`
      | `permissions.remove`
      | `permissions.update`
      | `revision`,
      4
    >
  ) =>
    universalUpdateDataCollection(
      collection,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDataCollectionSignature {
  /**
   * Updates a data collection.
   *
   * A collection ID, revision number, permissions, and at least 1 field must be provided in the `collection` body parameter.
   * If a collection with that ID exists, and if its current `revision` number matches the one provided, it's updated.
   * Otherwise, the request fails.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * After a collection is updated, it only contains the properties included in the Update Data Collection request.
   * If the existing collection has properties with values and those properties aren't included in the updated collection details,
   * their values are lost.<br/><br/>
   *
   * To update only specific properties without affecting others, use [Patch Data Collection](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection).
   * </blockquote>
   *
   * When a collection is updated, its `updatedDate` changes to the current date and its `revision` increments by 1.
   *
   * To update only properties in a collection's `fields` array, use [Update Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/update-data-collection-field).
   * To only add or remove `plugins`, use [Add Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/add-data-collection-plugin) or [Delete Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/delete-data-collection-plugin).
   * @param - Updated collection details. The existing collection is replaced with this version.
   * @param - Options for updating a data collection.
   * @returns Updated collection details.
   */
  (
    collection: NonNullablePaths<
      DataCollection,
      | `_id`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `permissions.insert`
      | `permissions.read`
      | `permissions.remove`
      | `permissions.update`
      | `revision`,
      4
    >
  ): Promise<
    NonNullablePaths<
      DataCollection,
      | `_id`
      | `collectionType`
      | `defaultDisplayOrder.fieldKey`
      | `defaultDisplayOrder.direction`
      | `capabilities.dataOperations`
      | `capabilities.collectionOperations`
      | `capabilities.indexLimits.regular`
      | `capabilities.indexLimits.unique`
      | `capabilities.indexLimits.total`
      | `fields`
      | `fields.${number}.key`
      | `fields.${number}.type`
      | `fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `fields.${number}.typeMetadata.array.elementType`
      | `fields.${number}.systemField`
      | `fields.${number}.capabilities.sortable`
      | `fields.${number}.encrypted`
      | `fields.${number}.condition.permission`
      | `permissions.insert`
      | `permissions.update`
      | `permissions.remove`
      | `permissions.read`
      | `plugins`
      | `plugins.${number}.publishOptions.defaultStatus`
      | `plugins.${number}.singleItemOptions.singleItemId`
      | `plugins.${number}.urlizedOptions.format`
      | `plugins.${number}.editablePageLinkOptions.isPersisted`
      | `plugins.${number}.editablePageLinkOptions.isMutable`
      | `plugins.${number}.type`
      | `pagingModes`,
      6
    >
  >;
}

export function deleteDataCollection(
  httpClient: HttpClient
): DeleteDataCollectionSignature {
  return (dataCollectionId: string) =>
    universalDeleteDataCollection(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDataCollectionSignature {
  /**
   * Deletes a data collection.
   *
   * Before deleting a collection:
   * - Make sure the collection isn't connected to any site pages or elements.
   * - You can create a manual backup of a collections with the Backups API, in case you need to restore them later.
   * - You can also export a collection to a CSV file with the Data Movement Jobs API, if you want to save the data outside of Wix.
   *
   * > **Note:**
   * > Once a collection is deleted, it can only be restored for a limited amount of time.
   * @param - ID of the collection to delete.
   */
  (dataCollectionId: string): Promise<void>;
}

/** @internal */
export function restoreDataCollection(
  httpClient: HttpClient
): RestoreDataCollectionSignature {
  return (dataCollectionId: string) =>
    universalRestoreDataCollection(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreDataCollectionSignature {
  /**
   * Restores recently deleted collection.
   *
   * Fails with `WDE0025` if collection was deleted too long ago or has never existed.
   * @param - Data Collection ID to restore
   */
  (dataCollectionId: string): Promise<
    NonNullablePaths<
      RestoreDataCollectionResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function patchDataCollection(
  httpClient: HttpClient
): PatchDataCollectionSignature {
  return (_id: string, dataCollection: PatchDataCollection) =>
    universalPatchDataCollection(
      _id,
      dataCollection,
      // @ts-ignore
      { httpClient }
    );
}

interface PatchDataCollectionSignature {
  /**
   * Updates specified properties of a data collection.
   *
   * Only updates the properties specified in the request, all other properties remain unchanged.
   * The following properties can be updated with this method: `displayName`, `displayField`, `permissions`, `dataPermissions`. If attempting to update other, unsupported properties, this operation fails.
   *
   * To update properties in a collection's `fields`array, use [Patch Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection-field).
   * To add or remove `plugins`, use [Add Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/add-data-collection-plugin) or [Delete Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/delete-data-collection-plugin).
   * @param - Collection ID. For example, `my-first-collection`. May include a namespace.
   */
  (_id: string, dataCollection: PatchDataCollection): Promise<
    NonNullablePaths<
      PatchDataCollectionResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function createDataCollectionField(
  httpClient: HttpClient
): CreateDataCollectionFieldSignature {
  return (
    dataCollectionId: string,
    options: NonNullablePaths<
      CreateDataCollectionFieldOptions,
      `field` | `field.key` | `field.type`,
      3
    >
  ) =>
    universalCreateDataCollectionField(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDataCollectionFieldSignature {
  /**
   * Adds a new field to the structure of a data collection.
   * @param - ID of the data collection to update.
   */
  (
    dataCollectionId: string,
    options: NonNullablePaths<
      CreateDataCollectionFieldOptions,
      `field` | `field.key` | `field.type`,
      3
    >
  ): Promise<
    NonNullablePaths<
      CreateDataCollectionFieldResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function updateDataCollectionField(
  httpClient: HttpClient
): UpdateDataCollectionFieldSignature {
  return (
    dataCollectionId: string,
    options: NonNullablePaths<
      UpdateDataCollectionFieldOptions,
      `field` | `field.key` | `field.type`,
      3
    >
  ) =>
    universalUpdateDataCollectionField(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDataCollectionFieldSignature {
  /**
   * Updates properties in a [data collection's](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/create-data-collection) `fields` array.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * After `fields` is updated, it only contains the properties included in the Update Data Collection Field request.
   * If the existing `fields` array has properties with values and those properties aren't included in the updated details, their values are lost.<br/><br/>
   *
   * To update only specific properties in the array without affecting others, use [Patch Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection-field).
   * </blockquote>
   * @param - ID of the data collection to update.
   */
  (
    dataCollectionId: string,
    options: NonNullablePaths<
      UpdateDataCollectionFieldOptions,
      `field` | `field.key` | `field.type`,
      3
    >
  ): Promise<
    NonNullablePaths<
      UpdateDataCollectionFieldResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function deleteDataCollectionField(
  httpClient: HttpClient
): DeleteDataCollectionFieldSignature {
  return (
    dataCollectionId: string,
    options: NonNullablePaths<DeleteDataCollectionFieldOptions, `fieldKey`, 2>
  ) =>
    universalDeleteDataCollectionField(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDataCollectionFieldSignature {
  /**
   * Deletes a field from the structure of a data collection.
   *
   * When a field is deleted, all values associated with that field are also deleted
   * from all items in the collection.
   * @param - ID of the data collection to update.
   */
  (
    dataCollectionId: string,
    options: NonNullablePaths<DeleteDataCollectionFieldOptions, `fieldKey`, 2>
  ): Promise<
    NonNullablePaths<
      DeleteDataCollectionFieldResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function addDataCollectionPlugin(
  httpClient: HttpClient
): AddDataCollectionPluginSignature {
  return (
    dataCollectionId: string,
    options: NonNullablePaths<
      AddDataCollectionPluginOptions,
      `plugin` | `plugin.type`,
      3
    >
  ) =>
    universalAddDataCollectionPlugin(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDataCollectionPluginSignature {
  /**
   * Adds a plugin to a data collection.
   *
   * If a plugin of the same type already exists in the collection, this operation fails.
   * @param - ID of the data collection to update.
   */
  (
    dataCollectionId: string,
    options: NonNullablePaths<
      AddDataCollectionPluginOptions,
      `plugin` | `plugin.type`,
      3
    >
  ): Promise<
    NonNullablePaths<
      AddDataCollectionPluginResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function deleteDataCollectionPlugin(
  httpClient: HttpClient
): DeleteDataCollectionPluginSignature {
  return (
    dataCollectionId: string,
    options: NonNullablePaths<
      DeleteDataCollectionPluginOptions,
      `pluginType`,
      2
    >
  ) =>
    universalDeleteDataCollectionPlugin(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDataCollectionPluginSignature {
  /**
   * Removes a plugin from a data collection.
   *
   * Removing a plugin disables its functionality for the collection.
   * @param - ID of the data collection to update.
   */
  (
    dataCollectionId: string,
    options: NonNullablePaths<
      DeleteDataCollectionPluginOptions,
      `pluginType`,
      2
    >
  ): Promise<
    NonNullablePaths<
      DeleteDataCollectionPluginResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export function patchDataCollectionField(
  httpClient: HttpClient
): PatchDataCollectionFieldSignature {
  return (dataCollectionId: string, field: Field) =>
    universalPatchDataCollectionField(
      dataCollectionId,
      field,
      // @ts-ignore
      { httpClient }
    );
}

interface PatchDataCollectionFieldSignature {
  /**
   * Updates specified properties in a [data collection's](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/create-data-collection) `fields` array.
   *
   * Only updates the properties specified in the request, all other properties remain unchanged.
   * The following properties can be updated with this method: `displayName`, `type`, `typeMetadata`, `encrypted`, `description`. If attempting to update `key` or other unsupported properties, this operation fails.
   *
   * To update properties in a data collection other than `fields`, use [Patch Data Collection](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection).
   * @param - ID of the data collection to update.
   * @param - Field to update.
   */
  (dataCollectionId: string, field: Field): Promise<
    NonNullablePaths<
      PatchDataCollectionFieldResponse,
      | `dataCollection._id`
      | `dataCollection.collectionType`
      | `dataCollection.defaultDisplayOrder.fieldKey`
      | `dataCollection.defaultDisplayOrder.direction`
      | `dataCollection.capabilities.dataOperations`
      | `dataCollection.capabilities.collectionOperations`
      | `dataCollection.capabilities.indexLimits.regular`
      | `dataCollection.capabilities.indexLimits.unique`
      | `dataCollection.capabilities.indexLimits.total`
      | `dataCollection.fields`
      | `dataCollection.fields.${number}.key`
      | `dataCollection.fields.${number}.type`
      | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
      | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
      | `dataCollection.fields.${number}.typeMetadata.array.elementType`
      | `dataCollection.fields.${number}.systemField`
      | `dataCollection.fields.${number}.capabilities.sortable`
      | `dataCollection.fields.${number}.encrypted`
      | `dataCollection.fields.${number}.condition.permission`
      | `dataCollection.permissions.insert`
      | `dataCollection.permissions.update`
      | `dataCollection.permissions.remove`
      | `dataCollection.permissions.read`
      | `dataCollection.plugins`
      | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
      | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
      | `dataCollection.plugins.${number}.urlizedOptions.format`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
      | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
      | `dataCollection.plugins.${number}.type`
      | `dataCollection.pagingModes`,
      7
    >
  >;
}

export {
  AccessLevel,
  AccountInfo,
  ActionEvent,
  AddDataCollectionPluginOptions,
  AddDataCollectionPluginRequest,
  AddDataCollectionPluginResponse,
  Address,
  AllowedDataPermissions,
  AppOptions,
  ApplicationError,
  ArrayDocument,
  ArraySizeRange,
  ArrayString,
  Audio,
  BulkGetDataCollectionsPageBySnapshotsRequest,
  BulkGetDataCollectionsPageBySnapshotsRequestTenantModeOptionsOneOf,
  BulkGetDataCollectionsPageBySnapshotsResponse,
  BulkGetDataCollectionsRequest,
  BulkGetDataCollectionsResponse,
  Calculator,
  CalculatorPatternOneOf,
  CatalogOptions,
  CategoryFieldOptions,
  CmsOptions,
  CollectionCapabilities,
  CollectionDefinition,
  CollectionOperation,
  CollectionType,
  CreateAppCollectionsSnapshotRequest,
  CreateAppCollectionsSnapshotResponse,
  CreateDataCollectionFieldOptions,
  CreateDataCollectionFieldRequest,
  CreateDataCollectionFieldResponse,
  CreateDataCollectionRequest,
  CreateDataCollectionResponse,
  CreateDataCollectionsSnapshotRequest,
  CreateDataCollectionsSnapshotResponse,
  CreateMigratedCollectionsSnapshotRequest,
  CreateMigratedCollectionsSnapshotResponse,
  DataCollection,
  DataCollectionChangedEvent,
  DataCollectionClonedEvent,
  DataCollectionEvictedEvent,
  DataOperation,
  DataPermissions,
  Datetime,
  DatetimeDefaultValueOneOf,
  DatetimeMaxDateOneOf,
  DatetimeMinDateOneOf,
  DeleteDataCollectionFieldOptions,
  DeleteDataCollectionFieldRequest,
  DeleteDataCollectionFieldResponse,
  DeleteDataCollectionPluginOptions,
  DeleteDataCollectionPluginRequest,
  DeleteDataCollectionPluginResponse,
  DeleteDataCollectionRequest,
  DeleteDataCollectionResponse,
  DeleteDataCollectionsSnapshotRequest,
  DeleteDataCollectionsSnapshotResponse,
  Destination,
  Direction,
  Document,
  DomainEvent,
  DomainEventBodyOneOf,
  Email,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EnumValues,
  FailedCollection,
  Failure,
  Field,
  FieldCapabilities,
  FieldCondition,
  FieldPlugin,
  FieldPluginOptionsOneOf,
  FieldPluginType,
  FieldRangeValidationsOneOf,
  FieldUpdate,
  FieldsPattern,
  Format,
  GetDataCollectionOptions,
  GetDataCollectionRequest,
  GetDataCollectionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  Index,
  IndexField,
  IndexLimits,
  IndexQuotaLimits,
  IndexSource,
  IndexStatus,
  LegacyColor,
  ListDataCollectionsOptions,
  ListDataCollectionsRequest,
  ListDataCollectionsResponse,
  MediaGallery,
  MessageEnvelope,
  MultiReference,
  MultilingualOptions,
  NumberRange,
  ObjectField,
  Order,
  PageLink,
  PageLinkPluginOptions,
  PageLinkType,
  Paging,
  PagingMetadataV2,
  PagingMode,
  PatchDataCollection,
  PatchDataCollectionFieldRequest,
  PatchDataCollectionFieldResponse,
  PatchDataCollectionRequest,
  PatchDataCollectionResponse,
  Permission,
  Permissions,
  PermissionsUpdate,
  Plugin,
  PluginCmsOptions,
  PluginOptionsOneOf,
  PluginType,
  PluginUpdate,
  PublishPluginOptions,
  QueryOperator,
  Reference,
  RestorationCollection,
  RestoreDataCollectionRequest,
  RestoreDataCollectionResponse,
  RestoreDataCollectionsFromSnapshotRequest,
  RestoreDataCollectionsFromSnapshotResponse,
  RestoreInfo,
  RichContent,
  RichText,
  Role,
  Segment,
  SingleItemPluginOptions,
  SiteSort,
  Slug,
  SnapshotCollection,
  Sort,
  SortOrder,
  Sorting,
  Status,
  StrictModeUpdate,
  StringLengthRange,
  TenantMode,
  Text,
  TextValidation,
  TextValidationOneOf,
  Time,
  Type,
  TypeMetadata,
  TypeMetadataMetadataOneOf,
  UpdateDataCollectionFieldOptions,
  UpdateDataCollectionFieldRequest,
  UpdateDataCollectionFieldResponse,
  UpdateDataCollectionRequest,
  UpdateDataCollectionResponse,
  UpdateDataPermissionsRequest,
  UpdateDataPermissionsResponse,
  Url,
  UrlizedOnlyPattern,
  UrlizedPluginOptions,
  Video,
  WebhookIdentityType,
  _Array,
  _Boolean,
  _Date,
  _DateDefaultValueOneOf,
  _DateMaxDateOneOf,
  _DateMinDateOneOf,
  _Number,
  _Object,
} from './data-v2-data-collection-collections.universal.js';
